import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { Spinner } from "../shared/Spinner";
import {
  BASE_INPUT,
  INPUT_WRAPPER,
  ITEM_CARD_FILTER_HEADER,
  ITEM_CARD_FILTER_MENUITEM,
  LOADING_WRAPPER,
  PRODUCT_FILTER_WRAPPER,
  PRODUCT_WRAPPER,
} from "../styles";
import ItemCard from "./ItemCard";
import FacetItems from "./FacetItems";
import NO_RECORDS from "../../assets/images/no-records.png";
import {
  getFilteredData,
  getProducts,
  getSearchData,
  getSuggestions,
} from "../../services/searchProducts";
import _ from "lodash";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import PRODUCT_PLACEHOLDER from "../../assets/images/product_placeholder.png";
import { view } from "@risingstack/react-easy-state";
import { ProductStore } from "../../store";
import { useNavigate } from "react-router";
import { ROUTES } from "../../routes/constants";

const SearchComponent = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isProductLoading, setIsProductLoading] =
    React.useState<boolean>(false);
  const [smartProducts, setSmartProducts] = React.useState<any[]>([]);
  const [facets, setFacets] = React.useState<any[]>([]);
  const [options, setOptions] = React.useState<any>([]);
  const [inputValue, setInputValue] = React.useState("");
  const [searchProducts, setSearchProducts] = React.useState<any[]>([]);
  const [isFilterLoading, setIsFilterLoading] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [firstOption, setFistOption] = React.useState<string>("");

  const getAutoSuggetions = async (searchText: string) => {
    try {
      const res = await getSuggestions(searchText);
      if (res && res?.data) {
        return res?.data || [];
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getSearchProducts = async (searchText: string) => {
    try {
      const res = await getProducts(searchText);
      if (res && res?.data) {
        return res?.data || [];
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getSearchProductsAndFacets = async (searchItem: string) => {
    try {
      setIsLoading(true);
      const res = await getSearchData(searchItem);
      if (res && res?.data) {
        const tempProducts = res?.data?.documents || [];
        const tempFacets = res?.data?.facets || [];
        setFacets(tempFacets);
        setSmartProducts(tempProducts);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("[getSearchProductsAndFacets]: ", error);
      setIsLoading(false);
    }
  };

  const fetchData = React.useMemo(
    () =>
      _.throttle((request) => {
        setIsFilterLoading(true);
        const tempSuggestions = getAutoSuggetions(request?.input);
        const tempProducts = getSearchProducts(request?.input);
        tempSuggestions
          .then((resSuggestions) => {
            tempProducts
              .then((resProducts) => {
                const tempProducts = resProducts?.documents || [];
                setSearchProducts(tempProducts);
                setOptions(resSuggestions);
                setFistOption(resSuggestions[0].text);
                setIsFilterLoading(false);
              })
              .catch(() => {
                setSearchProducts([]);
                setOptions(resSuggestions);
                setFistOption(resSuggestions[0].text);
                setIsFilterLoading(false);
              });
          })
          .catch(() => {
            setOptions([]);
            setFistOption("");
            setIsFilterLoading(false);
          });
      }, 200),
    []
  );

  const fetchProductsAndSuggestions = async (searchText: string) => {
    try {
      if (!_.isEmpty(searchText)) {
        fetchData({ input: searchText });
      } else {
        setOptions([]);
        setFistOption("");
        setSearchProducts([]);
      }
    } catch (error) {
      console.error("[fetchProductsAndSuggestions]: ", error);
    }
  };

  React.useEffect(() => {
    fetchProductsAndSuggestions(inputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const onSearchChange = async (value: any) => {
    setSearchTerm(value);
    if (value) {
      await getSearchProductsAndFacets(value.text);
    }
  };

  const getFilteredProducts = async (filters: any, key: string) => {
    setIsProductLoading(true);
    const filterPayload: any[] = [];
    for (const [k, v] of Object.entries(filters)) {
      filterPayload.push({
        filterName: k,
        filters: v,
      });
    }
    try {
      const filteredResult = await getFilteredData(
        inputValue,
        filterPayload,
        key
      );
      const tempProducts = filteredResult?.data?.documents || [];
      const tempFacets = filteredResult?.data?.facets || [];
      setFacets(tempFacets);
      setSmartProducts(tempProducts);
      setIsProductLoading(false);
    } catch (error) {
      console.error("[handleFilterToggle]: ", error);
      setIsProductLoading(false);
    }
  };

  const getImageUrl = (urls: any) => {
    if (_.isEmpty(urls) || urls.length === 0) {
      return PRODUCT_PLACEHOLDER;
    }
    return urls ? urls[0] : "";
  };

  const getItemTitle = (titles: any[]) => {
    if (_.isEmpty(titles) || titles.length === 0) {
      return "";
    } else {
      const tempTitle: any = titles.slice(0, 1);
      const matches = match(tempTitle[0], inputValue, {
        insideWords: true,
      });
      const parts = parse(tempTitle[0], matches);
      let noOption = false;
      if (
        parts.length === 1 &&
        tempTitle[0].toLowerCase().includes(firstOption.toLowerCase()) &&
        !_.isEmpty(_.find(parts, ["highlight", false]))
      ) {
        noOption = true;
      }
      return (
        <div style={{ alignSelf: "center", display: "felx" }}>
          {noOption ? (
            <span
              key={tempTitle[0]}
              style={{
                fontSize: 12,
                textAlign: "center",
                fontWeight: 600,
                color: "#0F3D61",
              }}
            >
              {tempTitle[0]}
            </span>
          ) : (
            parts.map((part, index) => (
              <span
                key={index}
                style={{
                  fontSize: 12,
                  textAlign: "center",
                  fontWeight: part.highlight ? 600 : 400,
                  color: part.highlight ? "#0F3D61" : "#6F7784",
                }}
              >
                {part.text}
              </span>
            ))
          )}
        </div>
      );
    }
  };

  const onProductClick = (item: any) => {
    ProductStore.setProductDetails(item);
    setTimeout(() => {
      navigate(ROUTES.PRODUCTDETAILS);
    }, 500);
  };

  return (
    <Box sx={PRODUCT_WRAPPER}>
      <Stack sx={PRODUCT_FILTER_WRAPPER}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"center"}
          flex={1}
        >
          <Autocomplete
            id="upc-autocomplete"
            getOptionLabel={(option: any) =>
              typeof option === "string" ? option : option.text
            }
            filterOptions={(x) => x}
            inputValue={inputValue}
            value={searchTerm}
            options={options}
            fullWidth
            autoHighlight
            style={{ width: "80%" }}
            PaperComponent={({ children }) => (
              <Paper>
                {isFilterLoading ? (
                  <Stack
                    direction={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    height={200}
                  >
                    <CircularProgress sx={{ color: "#276B8B" }} size={32} />
                    <Typography sx={{ color: "#276B8B" }} variant="body2">
                      Fetching data...
                    </Typography>
                  </Stack>
                ) : (
                  <Stack direction={"row"} spacing={1} flex={1}>
                    <Stack flex={0.6} height={"100%"}>
                      {children}
                    </Stack>
                    {!_.isEmpty(searchProducts) && (
                      <Stack flex={1}>
                        <Grid container>
                          {searchProducts
                            .splice(0, 9)
                            .map((sp: any, index: number) => (
                              <Grid
                                item
                                xs={2}
                                sm={4}
                                md={4}
                                key={`${sp.upc}_${index}`}
                                sx={{
                                  borderRight:
                                    "1px solid rgba(213, 215, 220, 0.74)",
                                  borderBottom:
                                    "1px solid rgba(213, 215, 220, 0.74)",
                                  padding: 2,
                                  cursor: "pointer",
                                }}
                                onMouseDown={() => onProductClick(sp)}
                              >
                                <Stack spacing={2} justifyContent={"center"}>
                                  <div
                                    style={{
                                      backgroundImage: `url(${getImageUrl(
                                        sp?.imageUrls
                                      )})`,
                                      backgroundRepeat: "no-repeat",
                                      backgroundPosition: "center",
                                      backgroundSize: "contain",
                                      height: "70px",
                                    }}
                                  />
                                  {getItemTitle(sp?.titles)}
                                </Stack>
                              </Grid>
                            ))}
                        </Grid>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Paper>
            )}
            onInputChange={(_event, newInputValue) => {
              setInputValue(newInputValue);
              setSearchTerm(newInputValue);
            }}
            onChange={(_event: any, newValue: string | null) => {
              onSearchChange(newValue);
            }}
            isOptionEqualToValue={(option: any, value: any) =>
              option.text === value.text
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ ...INPUT_WRAPPER, width: "100%", paddingY: 0 }}
                variant="standard"
                placeholder="Search..."
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  sx: { ...BASE_INPUT, height: 32 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#6F7784" }} fontSize={"small"} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option: any, { inputValue }) => {
              const matches = match(option?.text, inputValue, {
                insideWords: true,
              });
              const parts = parse(option?.text, matches);
              let noOption = false;
              if (
                parts.length === 1 &&
                option?.text.toLowerCase() === firstOption.toLowerCase() &&
                !_.isEmpty(_.find(parts, ["highlight", false]))
              ) {
                noOption = true;
              }

              return (
                <li {...props}>
                  <div>
                    {noOption ? (
                      <span
                        key={"no-highlighted-text"}
                        style={{
                          ...(ITEM_CARD_FILTER_MENUITEM as any),
                          fontWeight: 600,
                          color: "#0F3D61",
                        }}
                      >
                        {option.text}
                      </span>
                    ) : (
                      parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            ...(ITEM_CARD_FILTER_MENUITEM as any),
                            fontWeight: part.highlight ? 600 : 400,
                            color: part.highlight ? "#0F3D61" : "#282C36",
                          }}
                        >
                          {part.text}
                        </span>
                      ))
                    )}
                  </div>
                </li>
              );
            }}
          />
        </Stack>
      </Stack>
      <Stack direction="row">
        {isLoading ? (
          <Spinner />
        ) : smartProducts.length > 0 ? (
          <Stack direction={"row"} spacing={2} flex={1}>
            <Stack
              sx={{
                flex: 0.3,
                height: "calc(100vh - 200px)",
                overflow: "auto",
                background: "#FFFFFF",
                borderRadius: "8px",
                paddingY: 2,
              }}
            >
              <Typography
                sx={{
                  ...ITEM_CARD_FILTER_HEADER,
                  textAlign: "left",
                  paddingLeft: 2,
                }}
              >
                Filters
              </Typography>
              <FacetItems
                data={facets}
                onFilterProducts={getFilteredProducts}
              />
            </Stack>
            <Stack
              direction={"row"}
              flexWrap={"wrap"}
              flex={1}
              height={"calc(100vh - 200px)"}
              overflow={"auto"}
            >
              {isProductLoading ? (
                <Stack sx={LOADING_WRAPPER} flex={1}>
                  <CircularProgress color="inherit" />
                  <Typography color={"inherit"} variant="subtitle1">
                    Fetching Products...
                  </Typography>
                </Stack>
              ) : (
                smartProducts.map((p: any, index: any) => (
                  <ItemCard
                    data={p}
                    key={`${p.upc}-${index}`}
                    handleProductClick={() => onProductClick(p)}
                  />
                ))
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            flex={1}
          >
            <img src={NO_RECORDS} alt="No Records" height={204} width={204} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default view(SearchComponent);
